import React, { useState, useEffect, useRef } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Navbar from "components/Navbar";
import Sidebar from "components/Sidebar";
import { Box, useMediaQuery, Fab, useTheme, CircularProgress } from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import axios from "api/axios";
import { setUser, logoutUser, restoreViewAsRole, setTwoFactorRequired } from "state";
import ViewAsBanner from "components/ViewAsBanner";
import PresenceHeartbeat from "components/PresenceHeartbeat";
import SessionIdleGuard from "components/SessionIdleGuard";
import TwoFactorGate from "components/TwoFactorGate";

const Layout = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const user = useSelector((state) => state.global.user);
  const location = useLocation();
  const timezone = useSelector((state) => state.global.timezone);
  const twoFactorRequired = useSelector((state) => state.global.twoFactorRequired);
  const role = user?.role ?? "member";

  // Responsive: detect non-mobile screens (min-width: 600px)
  const isNonMobile = useMediaQuery("(min-width: 600px)");
  // Keep sidebar closed when zoomed / mid-width so content has room (user can reopen via menu).
  const preferSidebarOpen = useMediaQuery("(min-width: 1200px)");
  
  // Sidebar state - open by default only when there is comfortable width
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isCheckingAuth, setIsCheckingAuth] = useState(() => !user);
  const wasTwoFactorRequired = useRef(!!twoFactorRequired);
  const [contentEpoch, setContentEpoch] = useState(0);

  // Close sidebar on narrow / zoomed viewports
  useEffect(() => {
    setIsSidebarOpen(preferSidebarOpen);
  }, [preferSidebarOpen]);

  // Remount routed pages only after 2FA unlock (not when locking), so the
  // Interviews page stays visible under the blur instead of going blank.
  useEffect(() => {
    if (wasTwoFactorRequired.current && !twoFactorRequired) {
      setContentEpoch((n) => n + 1);
    }
    wasTwoFactorRequired.current = !!twoFactorRequired;
  }, [twoFactorRequired]);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Don't check auth if we just logged out
      if (sessionStorage.getItem("logout")) {
        sessionStorage.removeItem("logout");
        dispatch(logoutUser());
        setIsCheckingAuth(false);
        return;
      }
      
      try {
        // If no user in state, try to get from server
        if (!user) {
          const res = await axios.get("/auth/me");
          if (res.data) {
            dispatch(setUser(res.data));
            dispatch(restoreViewAsRole());
          } else {
            dispatch(logoutUser());
          }
        }
      } catch (err) {
        // Not authenticated - clear user and redirect will happen
        dispatch(logoutUser());
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Re-check SMS 2FA when the 2h cookie expires (poll + tab focus).
  useEffect(() => {
    if (!user) return undefined;

    const checkTwoFactor = async () => {
      try {
        const { data } = await axios.get("/auth/2fa/status");
        const required = !!data?.required;
        if (required !== !!twoFactorRequired) {
          dispatch(setTwoFactorRequired(required));
        }
      } catch {
        /* ignore transient errors */
      }
    };

    checkTwoFactor();
    const id = setInterval(checkTwoFactor, 60 * 1000);
    const onVis = () => {
      if (document.visibilityState === "visible") checkTwoFactor();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user?._id, twoFactorRequired, dispatch]);

  // Redirect to login if not authenticated
  if (isCheckingAuth) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Define route access map
  // Admin/Superadmin: All sections
  // Team Lead (lead): All except "admin"
  // Recharge is available to every role (break / practice piano).
  // Members: dashboard, calendar, kanban, notifications, feedback, finance, my-progress
  // Callers: Calendar, Interviews, Daily tasks, Notifications, Feedback, …
  // Assistant: coordination only (no finance, team management, or admin panel)
  const roleAccess = {
  admin: [
    "dashboard", "recharge", "calendar", "kanban",
    "admin", "performance", "tasks", "members", "applications",
    "transactions", "geography", "finance", "business",
    // "sync", "manual-entry", // Removed - manual entry disabled, sync data hidden
    "overview", "daily", "monthly", "breakdown",
    "callers", "interview-list", "hiring", "resume-ops",
    "notifications", "feedback",
    // "profile-mapping", // Temporarily hidden
  ],
  superadmin: [
    "dashboard", "recharge", "calendar", "kanban",
    "admin", "performance", "tasks", "members", "applications",
    "transactions", "geography", "finance", "business",
    // "sync", "manual-entry", // Removed - manual entry disabled, sync data hidden
    "overview", "daily", "monthly", "breakdown",
    "callers", "interview-list", "hiring", "resume-ops",
    "notifications", "feedback",
    // "profile-mapping", // Temporarily hidden
  ],
  lead: [
    "dashboard", "recharge", "calendar", "kanban",
    "performance", "members", "applications",
    "transactions", "geography", "finance", "business",
    // "sync", "manual-entry", // Removed - manual entry disabled, sync data hidden
    "overview", "daily", "monthly", "breakdown",
    "notifications", "feedback",
    "interview-schedule",
    // "profile-mapping", // Temporarily hidden
  ],
  member: ["dashboard", "recharge", "calendar", "kanban", "notifications", "feedback", "finance", "my-progress"],
  caller: ["dashboard", "recharge", "calendar", "interviews", "interview-list", "applications", "performance", "notifications", "feedback", "tasks", "finance"],
    assistant: [
      "dashboard",
      "recharge",
      "performance",
      "calendar",
      "applications",
      "interview-list",
      "interviews",
      "notifications",
      "feedback",
    ],
  };

  // Subscribe to the router so this check updates on navigation. Reading
  // window.location left the previous allow/deny decision in place until some
  // other state change (such as hiding the sidebar) re-rendered the layout,
  // which then replaced the page with a redirect that never painted.
  const currentPath = location.pathname.split("/").filter(Boolean)[0] || "dashboard";
  const allowedPaths = roleAccess[role];
  const allowed = Array.isArray(allowedPaths) && allowedPaths.includes(currentPath);
  const defaultPath = "/dashboard";

  return (
    <Box
      display="flex"
      width="100%"
      height="100%"
      overflow="hidden"
      sx={{
        ...(twoFactorRequired
          ? {
              "& > *:not(.two-factor-gate-root)": {
                filter: "blur(6px)",
                pointerEvents: "none",
                userSelect: "none",
              },
            }
          : null),
      }}
    >
      {/* Pass sidebar state to Sidebar */}
      <Box sx={{ "body[data-piano-focus='1'] &": { display: "none" } }}>
        <Sidebar
          drawerWidth="250px"
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          isNonMobile={isNonMobile}
        />
      </Box>

      <Box
        flexGrow={1}
        sx={{
          width: isNonMobile && isSidebarOpen ? "calc(100% - 250px)" : "100%",
          transition: "width 0.3s ease",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minWidth: 0,
          overflow: "hidden",
          "body[data-piano-focus='1'] &": {
            width: "100% !important",
          },
        }}
      >
        {/* Pass sidebar state to Navbar */}
        <Box
          sx={{
            "body[data-piano-focus='1'] &": { display: "none" },
          }}
        >
          <Navbar
            user={user}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <ViewAsBanner />
        </Box>
        <PresenceHeartbeat />
        <SessionIdleGuard />

        {/* Single app scroll surface — fills leftover space under navbar/banner */}
        <Box
          id="main-content-scroll"
          className="scrollbar-page"
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            overflowX: "hidden",
            overflowY: "auto",
          }}
        >
          {!allowed ? <Navigate to={defaultPath} replace /> : (
          <Box
            // Remount when display timezone changes, and after 2FA unlock so
            // pages can refetch. Do not remount on lock (avoids blank page).
            key={`${timezone}-${contentEpoch}`}
            sx={{
              width: "100%",
              maxWidth: 1800,
              mx: "auto",
              flex: "0 0 auto",
              minWidth: 0,
              boxSizing: "border-box",
              px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
              py: { xs: 1.5, sm: 2 },
            }}
          >
            {/* Always keep the page mounted. 2FA gate blurs/blocks interaction;
                unmounting Outlet previously wiped the Interviews page to blank. */}
            <Outlet />
          </Box>
        )}
        </Box>
      </Box>

      <Box className="two-factor-gate-root">
        <TwoFactorGate open={!!twoFactorRequired} />
      </Box>

      {/* Floating Menu Button - Only visible when sidebar is hidden */}
      {!isSidebarOpen && !twoFactorRequired && (
        <Fab
          size="medium"
          onClick={() => setIsSidebarOpen(true)}
          sx={{
            position: "fixed",
            bottom: "2rem",
            left: "1.5rem",
            backgroundColor: theme.palette.secondary.main,
            color: "#ffffff",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
            "&:hover": {
              backgroundColor: theme.palette.secondary.dark,
            },
            zIndex: 1000,
          }}
        >
          <MenuIcon />
        </Fab>
      )}
    </Box>
  );
};

export default Layout;
